/* ==========================================================================
   ZELICAT THEME — article.js
   Article page: reading-progress bar, table of contents built from the headings
   (with scroll-position highlighting), numbered headings, "tip" callouts and native share.
   Port of the mockup's initArticle(). Copy-link is handled by global.js ([data-copy-link]).
   ========================================================================== */
(() => {
  'use strict';
  if (!window.ZC) return;
  const { $, $$ } = window.ZC;

  let cleanup = null;

  const pad = (n) => (n < 10 ? '0' : '') + n;

  const slugify = (text) => text
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'section';

  /* "3. Create vertical spaces" -> <span class="aprose__num">03</span><span>Create vertical spaces</span> */
  const numberHeading = (h) => {
    const m = h.textContent.match(/^\s*(\d+)\s*[.)]\s+/);
    if (!m) return null;
    const walker = document.createTreeWalker(h, NodeFilter.SHOW_TEXT);
    const first = walker.nextNode();
    if (!first) return null;
    first.nodeValue = first.nodeValue.replace(/^\s*\d+\s*[.)]\s+/, '');
    const num = document.createElement('span');
    num.className = 'aprose__num';
    num.setAttribute('aria-hidden', 'true');
    num.textContent = pad(parseInt(m[1], 10));
    const label = document.createElement('span');
    while (h.firstChild) label.appendChild(h.firstChild);
    h.appendChild(num);
    h.appendChild(label);
    return true;
  };

  /* <p><strong>ZeliCat Tip:</strong> text</p> -> <aside class="tip"> */
  const buildTips = (prose) => {
    const prefix = (prose.getAttribute('data-tip-prefix') || '').trim().toLowerCase();
    const label = prose.getAttribute('data-tip-label') || '';
    if (!prefix) return;
    $$(':scope > p', prose).forEach((p) => {
      const lead = p.firstElementChild;
      if (!lead || !/^(STRONG|B)$/.test(lead.tagName)) return;
      if (p.textContent.trim().toLowerCase().indexOf(prefix) !== 0) return;
      if (lead.textContent.trim().toLowerCase().indexOf(prefix) !== 0) return;
      const body = p.cloneNode(true);
      body.removeChild(body.firstElementChild);
      body.innerHTML = body.innerHTML.replace(/^\s*[:：\-–—]?\s*/, '');
      const aside = document.createElement('aside');
      aside.className = 'tip';
      const span = document.createElement('span');
      span.className = 'tip__label';
      span.textContent = label || lead.textContent.replace(/[:：\s]+$/, '');
      aside.appendChild(span);
      aside.appendChild(body);
      p.replaceWith(aside);
    });
  };

  ZC.mount((root) => {
    const prose = $('[data-article-content]', root);
    if (!prose || !ZC.once(prose, 'article')) return;
    if (cleanup) { cleanup(); cleanup = null; }

    const scope = prose.closest('[data-article-root]') || document;
    const bar = $('[data-read-progress]', root) || $('[data-read-progress]');
    const toc = $('[data-toc]', scope);
    const tocList = toc ? $('[data-toc-list]', toc) : null;
    const includeH3 = !!toc && toc.getAttribute('data-include-h3') === 'true';
    const doNumber = prose.getAttribute('data-number-headings') === 'true';

    buildTips(prose);

    /* ------------------------------------------------ headings -> ids, numbers, table of contents */
    const heads = $$(includeH3 ? 'h2, h3' : 'h2', prose);
    const used = new Set($$('[id]').map((el) => el.id));
    const items = [];
    let numbered = 0;
    heads.forEach((h) => {
      const isNum = h.tagName === 'H2' && doNumber && numberHeading(h);
      if (isNum) numbered += 1;
      const clone = h.cloneNode(true);
      $$('.aprose__num', clone).forEach((n) => n.remove());
      if (!h.id) {
        const base = slugify(clone.textContent);
        let id = base, i = 2;
        while (used.has(id)) { id = base + '-' + i; i += 1; }
        h.id = id;
      }
      used.add(h.id);
      items.push({ h, level: h.tagName === 'H3' ? 3 : 2, numbered: !!isNum, text: clone.textContent.trim() });
    });
    if (numbered) items.forEach((it) => { if (it.level === 2 && !it.numbered) it.h.classList.add('aprose__end'); });

    const links = [];
    if (tocList) {
      tocList.textContent = '';
      let k = 0;
      items.forEach((it) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.href = '#' + it.h.id;
        a.setAttribute('data-toc-link', '');
        a.setAttribute('data-level', String(it.level));
        if (it.level === 2) {
          const n = document.createElement('span');
          if (it.numbered) { k += 1; n.textContent = pad(k); }
          else if (numbered) n.textContent = '—';
          else { k += 1; n.textContent = pad(k); }
          a.appendChild(n);
        }
        a.appendChild(document.createTextNode(it.text.replace(/^\s*\d+\s*[.)]\s+/, '')));
        li.appendChild(a);
        tocList.appendChild(li);
        links.push({ a, h: it.h });
      });
      if (toc && !links.length) toc.hidden = true;
    }

    /* ------------------------------------------------ progress bar + active heading */
    const update = () => {
      if (bar) {
        const r = prose.getBoundingClientRect();
        const total = r.height - window.innerHeight * 0.6;
        const p = total > 0 ? Math.min(1, Math.max(0, (-r.top + window.innerHeight * 0.2) / total)) : 0;
        bar.style.transform = 'scaleX(' + p.toFixed(4) + ')';
      }
      if (!links.length) return;
      let cur = null;
      const line = window.innerHeight * 0.3;
      links.forEach((l) => { if (l.h.getBoundingClientRect().top <= line) cur = l.h.id; });
      links.forEach((l) => {
        if (l.a.getAttribute('href') === '#' + cur) l.a.setAttribute('aria-current', 'true');
        else l.a.removeAttribute('aria-current');
      });
    };
    let tick = false;
    const onScroll = () => { if (!tick) { tick = true; requestAnimationFrame(() => { update(); tick = false; }); } };
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', update);
    update();

    /* ------------------------------------------------ table of contents: open on desktop, collapsed on smaller screens */
    let mq = null;
    let sync = null;
    if (toc) {
      mq = window.matchMedia('(min-width: 1100px)');
      sync = () => { if (mq.matches) toc.setAttribute('open', ''); else toc.removeAttribute('open'); };
      sync();
      if (mq.addEventListener) mq.addEventListener('change', sync); else mq.addListener(sync);
      const summary = $('.toc__title', toc);
      if (summary) summary.addEventListener('click', (e) => { if (mq.matches) e.preventDefault(); });
      links.forEach((l) => l.a.addEventListener('click', () => { if (!mq.matches) toc.removeAttribute('open'); }));
    }

    /* ------------------------------------------------ native share (copy link lives in global.js) */
    const nat = $('[data-native-share]', scope);
    if (nat && navigator.share) {
      nat.hidden = false;
      const share = nat.closest('[data-share]');
      nat.addEventListener('click', () => {
        navigator.share({
          title: (share && share.getAttribute('data-share-title')) || document.title,
          url: location.href.split('#')[0]
        }).catch(() => {});
      });
    }

    cleanup = () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', update);
      if (mq && sync) { if (mq.removeEventListener) mq.removeEventListener('change', sync); else mq.removeListener(sync); }
    };
  });
})();
